import React, { useState } from "react";
import { FEATURES, FeatureAvailability, FeatureFlags } from "../featureAvailability";
import { Toggle } from "./Toggle";
import { LoadingState } from "./LoadingState";
import { FeatureAvailabilityError, TrainingNotice } from "./FeatureGate";

export const FeatureAvailabilityPage = ({ audience, data, error, onRetry, onSave, onBack }: {
  audience: string;
  data: FeatureAvailability | null;
  error: string | null;
  onRetry: () => void;
  onSave: (features: FeatureFlags) => Promise<void>;
  onBack: () => void;
}) => {
  const [draft, setDraft] = useState<FeatureFlags | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const flags = draft || data?.features;
  const changed = !!data && !!draft && FEATURES.some(({ id }) => draft[id] !== data.features[id]);
  const enabledCount = flags ? FEATURES.filter(({ id }) => flags[id]).length : 0;

  const save = async () => {
    if (!flags) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      await onSave(flags);
      setDraft(null);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Unable to save feature availability.");
    } finally {
      setSaving(false);
    }
  };

  return <section className="page feature-availability-page">
    <button type="button" className="secondary-btn feature-availability-back" onClick={onBack}>← Tools</button>
    <header className="page-header">
      <div>
        <h1>Feature Availability</h1>
        <p className="page-subtitle">Enable each feature when your audience is ready.</p>
      </div>
    </header>
    {error ? <FeatureAvailabilityError message={error} onRetry={onRetry} /> : !data || !flags ? (
      <LoadingState label="Loading feature settings…" layout="page" />
    ) : <>
      <div className="feature-availability-summary">
        <div><span className="feature-availability-label">Audience</span><strong>{audience}</strong></div>
        <span className="feature-availability-count">{enabledCount} of {FEATURES.length} features enabled</span>
      </div>
      <div className="feature-availability-layout">
        <div className="card feature-availability-list">
          <div className="feature-availability-list__header"><h2>Available features</h2><span>ON / OFF</span></div>
          {FEATURES.map((feature) => <div className="feature-availability-row" key={feature.id}>
            <div><label htmlFor={`feature-${feature.id}`}>{feature.label}</label><p>{feature.description}</p></div>
            <div className="feature-availability-control">
              <span className={flags[feature.id] ? "feature-availability-state is-on" : "feature-availability-state"}>{flags[feature.id] ? "ON" : "OFF"}</span>
              <Toggle id={`feature-${feature.id}`} ariaLabel={feature.label} checked={flags[feature.id]} disabled={saving}
                onChange={(checked) => { setDraft({ ...flags, [feature.id]: checked }); setSaved(false); }} />
            </div>
          </div>)}
          <footer className="feature-availability-actions">
            <span role="status">{saving ? "Saving changes…" : saved ? "Changes saved" : changed ? "Unsaved changes" : "All changes saved"}</span>
            <div>
              <button type="button" className="secondary-btn" disabled={!changed || saving} onClick={() => { setDraft(null); setSaveError(null); }}>Discard</button>
              <button type="button" className="primary-btn" disabled={!changed || saving} onClick={() => { void save(); }}>{saving ? "Saving…" : "Save changes"}</button>
            </div>
          </footer>
          {saveError && <p className="feature-availability-save-error" role="alert">{saveError}</p>}
        </div>
        <aside className="feature-availability-preview">
          <span className="feature-availability-label">What your audience sees</span>
          <h2>A clear next step</h2>
          <p>Sidebar links stay visible. Opening a disabled feature displays this message in place of the feature.</p>
          <TrainingNotice />
          <p className="feature-availability-note">Settings apply to client users in <strong>{audience}</strong>. Bellomy users can always access every feature, even when it is OFF.</p>
        </aside>
      </div>
    </>}
  </section>;
};
